import { isArray } from 'lodash-es';

import { HttpErrorResponse } from '@angular/common/http';

import { ErrorCodes, RawHttpErrorResponse } from '../models';
import { CustomHttpError } from '../models/custom-http-error.model';

export class MwHttpErrorParser {
  private readonly ErrorStringMap: { [key: string]: ErrorCodes } = {
    ['invalid_grant']: ErrorCodes.InvalidGrant,
  };
  private readonly ResponseStatusCodeMap: { [key: number]: ErrorCodes } = {
    400: ErrorCodes.BadRequest,
    404: ErrorCodes.NotFound,
    401: ErrorCodes.Unauthorized,
    403: ErrorCodes.Forbidden,
    500: ErrorCodes.ServerError,
  };

  parse(
    data: HttpErrorResponse | CustomHttpError | Error
  ): RawHttpErrorResponse {
    let result: RawHttpErrorResponse = new RawHttpErrorResponse(data);

    if (data instanceof CustomHttpError) {
      result = this.parseCustomHttpError(data);
    } else if (data instanceof Error) {
      result = this.parseError(data);
    } else if (data instanceof HttpErrorResponse) {
      result = this.parseHttpErrorResponse(data);
    }

    return result;
  }

  private parseCustomHttpError(data: CustomHttpError): RawHttpErrorResponse {
    const result: RawHttpErrorResponse = new RawHttpErrorResponse(data);
    const generalException = data;
    result.addError({
      errorCode: generalException.errorCode,
      message: generalException.message || '',
      memberName: '',
    });
    return result;
  }

  private parseError(data: Error): RawHttpErrorResponse {
    const result: RawHttpErrorResponse = new RawHttpErrorResponse(data);
    result.isException = true;
    result.addError({
      errorCode: ErrorCodes.Unknown,
      message: `${data.name}: ${data.message}`,
      memberName: '',
    });
    console.error(`${data.name}: ${data.message} \r\n ${data.stack}`);

    return result;
  }

  private parseHttpErrorResponse(
    data: HttpErrorResponse
  ): RawHttpErrorResponse {
    const errorCode = this.toErrorCode(data);
    const result = new RawHttpErrorResponse(data);
    const errors = data.error?.errors;
    result.isException = !!data.error?.exceptionTag;
    result.exceptionTag = data.error?.exceptionTag;

    if (errors && !isArray(errors)) {
      Object.keys(errors).forEach((key) => {
        result.addError({
          errorCode,
          message: `${key}: ${errors[key]}`,
          memberName: '',
        });
      });
    } else if (isArray(errors)) {
      (
        errors as Array<{
          errorCode: ErrorCodes;
          message: string;
          memberName: string;
        }>
      ).forEach((errorItem) =>
        result.addError({
          errorCode: errorItem.errorCode,
          message: errorItem.message,
          memberName: errorItem.memberName ?? '',
        })
      );
    } else {
      const message = this.geHttpErrorResponseErrorMessage(data);
      const rawErrorItem = result.addError({
        errorCode,
        message,
        memberName: '',
      });
      rawErrorItem.details = this.geHttpErrorResponseErrorMessageDetails(data);
    }

    return result;
  }

  private geHttpErrorResponseErrorMessage(data: HttpErrorResponse): string {
    if (data.status === 404) {
      return `${data.statusText} ${data.url}`;
    }

    const mainErrorField = data?.error;

    return (
      mainErrorField?.error_description ||
      mainErrorField?.message ||
      (mainErrorField?.exceptionTag &&
        `exceptionTag: ${data.error.exceptionTag}`) ||
      (typeof mainErrorField === 'string' && mainErrorField) ||
      mainErrorField?.title ||
      (mainErrorField?.errors && mainErrorField?.errors[0]?.message) ||
      data.message
    );
  }

  private geHttpErrorResponseErrorMessageDetails(
    data: HttpErrorResponse
  ): string {
    const mainErrorField = data?.error;

    return (
      mainErrorField?.messageDetail ||
      mainErrorField?.errors ||
      mainErrorField?.detail ||
      mainErrorField
    );
  }

  private toErrorCode(data: HttpErrorResponse): ErrorCodes {
    const errorCode =
      data.error && data.error.errors && data.error.errors[0]?.errorCode;
    const errorString = data.error && data.error.error;
    return (
      errorCode ||
      this.ErrorStringMap[errorString] ||
      this.ResponseStatusCodeMap[data.status] ||
      ErrorCodes.Unknown
    );
  }
}
